<!--
     <IndicatorRiskInterpreter
       :props="my_attrs_dictionary"
     />

     Public propertys:
       value → Gives the field's serializable value

     Public methods:
       validate → Makes validation and returns if the value is valid

     Accepted props:
     - value
     - size
     - label
     - required
     - readonly
     - class
     - disabled
-->

<template>
  <div class="iri-cont">
    <header class="imi">
      <h1 class="imi">{{ props.label }}</h1>

      <v-btn
        color="primary"
        small
        rounded
        class="add-button-fab explorer-header-component elevation-1"
        @click="add_range"
        :elevation="1"
        :disabled="props.disabled"
      >
        <span>{{ $vuetify.lang.translator('add') }}</span>
        <v-icon >mdi-plus</v-icon>
      </v-btn>
    </header>

    <div class="iri-list">
      <div class="iri-inter" v-for="(inter, index) of risk_interpreter" :key="inter.vue">
        <div class="iri-inter-id">{{ index + 1 }}</div>
        <div class="iri-inter-min">
          <v-text-field
            :value="inter.min"
            @change="(value) => change_inter(index, 'min', +value)"
            type="number"
            placeholder="Min"
            class="qmkey-form-input"
            :readonly="index > 0"
            :disabled="props.disabled || index > 0"
            outlined
            dense
          />
        </div>
        <div class="iri-inter-max">
          <v-text-field
            :value="inter.max"
            @change="(value) => change_inter(index, 'max', +value)"
            :rules="[(value) => max_rules(index, +value)]"
            type="number"
            placeholder="Max"
            class="qmkey-form-input"
            outlined
            dense
            :disabled="props.disabled"
          />
        </div>
        <div class="iri-inter-color">
          <SimpleColorPicker
            :value="inter.color"
            @update="(value) => change_inter(index, 'color', value)"
            :disabled="props.disabled"
          />
        </div>
        <div class="iri-inter-label">
          <v-text-field
            :value="inter.label"
            @change="(value) => change_inter(index, 'label', value)"
            placeholder="Label"
            class="qmkey-form-input"
            outlined
            dense
            :disabled="props.disabled"
          />
        </div>
        <div class="iri-inter-important">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                :color="inter.important ? 'primary' : 'grey'"
                v-bind="attrs"
                v-on="on"
                @click="() => change_inter(index, 'important', !inter.important)"
              >
                <v-icon>mdi-star</v-icon>
              </v-btn>
            </template>
            <span>Significativo</span>
          </v-tooltip>
        </div>
        <div class="iri-inter-close">
          <v-btn
            style="width: 100%"
            @click.stop="!props.disabled && delete_range(index)"
            :disabled="props.disabled"
          >
            x
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
 @import '@/sass/modalform.scss';
 @import "@/sass/variables.scss";

 .iri-cont {
   width: 100%;
   border-style: solid;
   border-width: 1px;
   border-color: $primary;
   border-radius: 5px;
   display: flex;
   flex-direction: column;
   padding: 8px;
   gap: 10px;
 }

 header.imi {
   display: flex;
   flex-direction: row;
   width: 100%;
   align-items: center;
   justify-content: space-between;
 }

 h1.imi {
   font-size: 1.2em;
   font-weight: normal;
 }

 .iri-list {
   display: flex;
   flex-direction: column;
   gap: 10px;
 }

 .iri-inter {
   width: 100%;
   border-style: solid;
   border-width: 0px 0px 0px 0px;
   border-color: #cacaca;
   border-radius: 5px;
   display: flex;
   flex-direction: row;
   padding: 8px;
   justify-content: flex-start;
   align-items: center;
   gap: 10px;
 }

 .iri-inter > div {
   display: block;
   max-height: 42px;
 }

 .iri-inter-id {
   width: 32px;
   color: #444;
 }

 .iri-inter-min,
 .iri-inter-max {
   width: 192px;
 }

 .iri-inter-close {
 }

 .iri-inter-label {
   width: 90%;
 }

 .iri-inter-color {
   width: 92px;
 }

 imi.button {
   
 }
</style>

<script>
 import SimpleColorPicker from '@/components/SimpleColorPicker';

 const DEFAULT_PRESET = [
   {vue: 0, min: 0, max: 33, color: '#5f5', label: 'Bajo'},
   {vue: 1, min: 33, max: 67, color: '#ff5', label: 'Medio'},
   {vue: 2, min: 67, max: 100, color: '#f55', label: 'Alto'},
 ];

 let vue_counter = 1;

 export default {
   name: 'IndicatorRiskInterpreter',

   props: ['props', 'required_rule'],

   components: {
     SimpleColorPicker,
   },

   data: () => ({
     valid: true,
     risk_interpreter: [],
     metric_edit_fix_type: false,
   }),

   mounted() {
     if (this.props.value) {
       this.pull_from_value(this.props.value);
     } else {
       this.pull_from_value(DEFAULT_PRESET.map(p => ({...p, vue: vue_counter++})));
       this.update();
     }
   },

   computed: {
     props_value() {
       return this.props.value;
     },

     metric_edit_modal() {
       return this.$refs.metric_edit_modal;
     },

     rules() {
       return [(value) => (
         (!!value && value.length > 0)
         || !this.props.required
         || this.$vuetify.lang.translator('required_field')
       )];
     },
   },

   methods: {
     risk_interpreter_sorted() {
       return this.risk_interpreter.slice().sort(
         (a, b) => ((a.min - b.min) || (a.max - b.max))
       );
     },

     serialize() {
       return this.risk_interpreter_sorted().map(r => ({
         'min': r.min,
         'max': r.max,
         'label': r.label || '',
         'color': (r.color || '#ffffff').toLowerCase(),
       }));
     },

     pull_from_value(val) { // TODO
       this.risk_interpreter = Array.from(val || []);
     },

     add_range() {
       const last_min = (this.risk_interpreter.length > 0 ? this.risk_interpreter.at(-1).min : 0);
       const last_max = (this.risk_interpreter.length > 0 ? this.risk_interpreter.at(-1).max : 0);
       this.risk_interpreter.push({
         /* "vue" parameter is to tell vue when this dictionary has been modified.
          *  It is the "key" in the v-for.
          *  Increment it with vue_counter after every change. */
         'vue': vue_counter++,
         'min': last_max,
         'max': 2*last_max - last_min,
         'label': 'Texto',
         'color': '#5af',
       });
       this.update();
     },

     delete_range(index) {
       this.risk_interpreter.splice(index, 1);
       this.update();
     },

     change_inter(index, key, value) {
       this.risk_interpreter[index][key] = value;
       this.risk_interpreter[index].vue = vue_counter++;
       if (key == 'max') {
         // Adjust minimum of the next range
         if (index+1 < this.risk_interpreter.length) {
           this.risk_interpreter[index+1].min = value;
           this.risk_interpreter[index+1].vue = vue_counter++;
         }
       }
       this.update();
     },

     max_rules(index, value) {
       // Vue rules. Returns string on error
       if (this.risk_interpreter[index].min > value) {
         this.error_state(false);
         return "Invalid";
       }
       this.error_state(true);
       return true;
     },

     error_state(ok) {
       this.valid = ok;
     },

     validate() {
       const ranges = this.risk_interpreter;
       let ok = true;
       for (let i = 1; i < ranges.length && ok; i++) {
         ok = (
           (ranges[i].min == ranges[i-1].max)
           && (ranges[i].max >= ranges[i].min)
         );
       }
       return ok;
     },

     update(value) {
       if (value === undefined) {
         value = this.serialize();
       }
       this.$emit('update', value);
     },
   },

   watch: {
     props_value() {
       if (!this.props_value) {
         this.pull_from_value(this.props_value)
       }
     },
   },
 }
</script>
